
import { useCallback, useMemo } from 'react'

import { useApp } from '../../../app/AppContext.jsx'
import { useCatalog } from '../../catalog/context/CatalogContext.jsx'


import { SECTION_ID } from '../../../shared/constants/sectionIds.js'

import {
	MOBILE_DESTINATION_ID,
	resolveActiveMobileDestination,
	resolveHeroViewForDestination,
	resolveMobileDestinations,
} from '../utils/mobileNavDestinations.js'

function resolveSectionIdForDestination(destinationId) {
	switch (destinationId) {
		case MOBILE_DESTINATION_ID.VISUALIZATION:
		case MOBILE_DESTINATION_ID.MODEL:
			return SECTION_ID.HERO
		case MOBILE_DESTINATION_ID.TIERS:
			return SECTION_ID.TIERS
		case MOBILE_DESTINATION_ID.PRODUCT:
			return SECTION_ID.PRODUCT
		case MOBILE_DESTINATION_ID.FAVORITES:
			return SECTION_ID.FAVORITES
		case MOBILE_DESTINATION_ID.CATALOG:
		default:
			return SECTION_ID.CATALOG
	}
}

export function useMobileNavDestinations() {
	const {
		activeSectionId,
		activeHeroView,
		activeProduct,
		appCapabilities,
		heroCapabilities,
		projectConfig,
		panelSettings,
		activateSectionHandler,
		changeHeroViewHandler,
		selectProductHandler,
	} = useApp()
  const { filteredProducts } = useCatalog()


	const destinations = useMemo(
		() => resolveMobileDestinations({ appCapabilities, heroCapabilities }),
		[appCapabilities, heroCapabilities],
	)

	const activeDestinationId = useMemo(
		() => resolveActiveMobileDestination(activeSectionId, activeHeroView),
		[activeHeroView, activeSectionId],
	)

	const navigateToDestination = useCallback((destinationId) => {
		const sectionId = resolveSectionIdForDestination(destinationId)

		if (sectionId === SECTION_ID.PRODUCT && !activeProduct) {
			const firstProduct = Array.isArray(filteredProducts) ? filteredProducts[0] : null
			const firstProductKey = firstProduct?.uuid ?? firstProduct?.id_cad ?? null

			if (firstProductKey) {
				selectProductHandler(firstProductKey, {
					buildingId: firstProduct?.building_id ?? null,
					refTier: firstProduct?.ref_tier ?? firstProduct?.tiers?.[0]?.ref_tier ?? null,
					scrollTarget: SECTION_ID.PRODUCT,
				})
				return
			}
		}

		if (sectionId === SECTION_ID.HERO) {
			const nextHeroView = resolveHeroViewForDestination(destinationId, {
				heroActiveView: activeHeroView,
				heroCapabilities,
			})

			if (nextHeroView) {
				changeHeroViewHandler(nextHeroView)
			}
		}

		activateSectionHandler(sectionId, { scrollTarget: sectionId })
	}, [
		activateSectionHandler,
		activeProduct,
		activeHeroView,
		changeHeroViewHandler,
		filteredProducts,
		heroCapabilities,
		selectProductHandler,
	])

	return {
		destinations,
		activeDestinationId,
		navigateToDestination,
	}
}